

















import { Vue, Component, Watch } from 'vue-property-decorator';
import { ClientComponent, pageResolver } from '@/core/app/componentResolver.service';
import appStore from '@/core/app/app.store';
import { NavigationItem, PageData } from '@/types/contentServerContract';
import { ContentPageRenderedEventKey } from '@/router';
import bus from '@/core/bus';
import contentApi from '@/core/http/contentApi.service';
import { HttpCancelError } from '@/core/http/HttpCancelError';
import logService from '@/core/logging.service';
import { LOGGED_IN } from '@/core/auth/auth.service';
import { Route } from 'vue-router';
import WithSideNavigation from '@/project/layout/navigation/WithSideNavigation.vue';
import SideNavigation from '@/project/layout/navigation/SideNavigation.vue';
import domService from '@/core/dom/dom.service';
import { NOT_FOUND } from 'http-status-codes';
import errorCodeService from '@/core/errorCode.service';
import themeService from '@/project/app/themes/theme.service';
import gtmService from '@/project/tracking/gtm/GTM.service';
import botService from '@/project/site/bot.service';
import serverContext from '@/core/serverContext.service';

const PAGE_NAME = 'content';

@Component({
    components: { WithSideNavigation, SideNavigation }
})
export default class ContentPageRenderer extends Vue {
    private pageData: PageData | null = null;
    private page: ClientComponent | null = null;

    private get key(): any | null {
        return this.pageData?.metadata?.key ?? Date.now().toString();
    }

    private get alias(): any | null {
        return this.pageData?.jsonContent?.alias ?? null;
    }

    private get content(): any | null {
        return this.pageData?.jsonContent?.content ?? null;
    }

    private get isTakeOverEnabled() {
        return themeService.isTakeOverEnabled;
    }

    private get isContainerEnabled(): boolean {
        return this.isTakeOverEnabled === false && appStore.isContainerDisabled === false;
    }

    private get hasLeftMenu() {
        return this.navigationItems.length > 0;
    }

    private get navigationItems(): NavigationItem[] {
        return this.pageData?.navigation.leftMenu ?? [];
    }

    private renderNewPage() {
        if (!this.pageData) return;

        appStore.initPage({ breadcrumb: this.pageData?.navigation?.breadcrumb });
        domService.updateMetaData(this.pageData.metadata, true);

        this.$nextTick(() => {
            if (this.pageData?.metadata?.id) {
                gtmService.trackPageRendered({ pageType: PAGE_NAME, pageId: this.pageData.metadata.id });
                gtmService.trackGenericPageView(PAGE_NAME, this.pageData.metadata.id);
            }

            // Fire event for router (scrolling).
            bus.emit(ContentPageRenderedEventKey);
        });
    }

    private async getPageData(path: string) {
        try {
            this.pageData = await contentApi.getPage(path);
            const expectedContentUrl = this.pageData?.metadata?.url || null;
            botService.setPrerenderRedirectUrlsIfLocationIsWrong(expectedContentUrl);
            this.renderNewPage();
        } catch (error) {
            if (error instanceof HttpCancelError) {
                // Ignore - just because of new page-request while waiting.
            } else if (error && ((error.status === NOT_FOUND) || (error.status === undefined && botService.uaIsPrerender() /* CORS or Network error */))) {
                // if page not found
                // or
                // isPrerender AND CORS/network error: CORS cannot be detected in Axios, will just show as network error.
                // Prevents odd URLs like https://www.eetgroup.com/en-eu/https:/content.eetgroup.com/de-de/brands/hikvision to get in prerender index.
                errorCodeService.handleErrorCodes(NOT_FOUND);
            } else {
                logService.exception('Got exception when loading page', error);
                this.page = null;
                this.pageData = null;
            }
        }
    }

    private async updatePageData() {
        await this.getPageData(this.$route.fullPath);
    }

    private async updateCurrentPage() {
        try {
            this.pageData = await contentApi.getPage(this.$route.fullPath);
            const expectedContentUrl = this.pageData?.metadata?.url ? `${serverContext.siteInfo.url}${this.pageData.metadata.url}` : null;
            botService.setPrerenderRedirectUrlsIfLocationIsWrong(expectedContentUrl);
            appStore.initPage({ breadcrumb: this.pageData.navigation.breadcrumb });
            domService.updateMetaData(this.pageData.metadata, true);
        } catch (error) {
            if (error instanceof HttpCancelError) {
                // Ignore - just because of new page-request while waiting.
            } else if (error && error.status === NOT_FOUND) {
                errorCodeService.handleErrorCodes(NOT_FOUND);
            } else {
                logService.exception('Got exception when loading page', error);
                this.pageData = null;
            }
        }
    }

    @Watch('alias')
    onResolvedPageChange(value: ClientComponent | null) {
        if (value) {
            this.page = pageResolver.resolve(this.alias);

            if (!this.page) {
                errorCodeService.handleErrorCodes(NOT_FOUND, 'redirecting to 404 - pagealias unknown: ' + value);
            }
        } else {
            this.page = null;
            this.pageData = null;
        }
    }

    @Watch('$route')
    private onRouteChange(to: Route, from: Route) {
        if (to.path === from.path) {
            if (this.page?.options?.updateOnQueryChange) {
                this.updateCurrentPage();
            }
            return;
        }

        if (this.pageData?.metadata?.id) {
            gtmService.trackPageDestroyed({ pageType: PAGE_NAME, pageId: this.pageData.metadata.id });
        }

        this.getPageData(to.fullPath);
    }

    private created() {
        bus.on(LOGGED_IN, this.updatePageData);
        this.updatePageData();
    }

    private destroyed() {
        if (this.pageData?.metadata?.key) {
            gtmService.trackPageDestroyed({ pageType: PAGE_NAME, pageId: this.pageData.metadata.id });
        }

        bus.off(LOGGED_IN, this.updatePageData);
    }
}
